/**
 * Merchant Weight Dispute API module.
 *
 * OMS speaks snake_case on the wire; every response is deep-camelized here at the choke point so
 * pages/components only ever see camelCase (same pattern the admin portal uses). All calls hit
 * `/v3/oms/merchant/weight-dispute/*` with the merchant JWT as a raw `Authorization` header
 * (no Bearer prefix — panel convention).
 *
 * Evidence upload is a 3-step presigned flow:
 *   requestEvidenceUpload → uploadEvidenceToS3 (bare axios, NO auth header) → confirmEvidenceUpload
 */
import axios from "axios";

const OMS_BASE = (process.env.REACT_APP_OMS_BASE_URL || "").trim();
const WD_BASE = `${OMS_BASE}/v3/oms/merchant/weight-dispute`;

// ---------- wire-format normalization ----------

const toCamel = (key) => key.replace(/_([a-z0-9])/g, (_, ch) => ch.toUpperCase());

const camelizeDeep = (value) => {
  if (Array.isArray(value)) return value.map(camelizeDeep);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [toCamel(k), camelizeDeep(v)])
    );
  }
  return value;
};

const authHeaders = (token) => (token ? { Authorization: token } : {});

/**
 * Normalize the raw snake_case `weight_disputes` array EMBEDDED in another endpoint's response
 * (the merchant order-tracking history carries it) into the same camelCase shape as this module.
 */
export const normalizeWeightDisputes = (raw) => (Array.isArray(raw) ? camelizeDeep(raw) : []);

// ---------- reads ----------

export const fetchSummary = async (token) => {
  const res = await axios.get(`${WD_BASE}/summary`, { headers: authHeaders(token), timeout: 30000 });
  return camelizeDeep(res.data);
};

/**
 * filters: { pageNumber, pageSize, status, awbNumber, orderReferenceNumber,
 *            createdFromDateTime, createdToDateTime } — exact backend @RequestParam names,
 * camelCase on the query string.
 */
export const fetchDisputes = async (token, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, val]) => {
    if (val !== undefined && val !== null && `${val}`.trim() !== "") params.set(key, `${val}`.trim());
  });
  const res = await axios.get(`${WD_BASE}/list?${params.toString()}`, {
    headers: authHeaders(token),
    timeout: 60000,
  });
  return camelizeDeep(res.data);
};

export const fetchDisputeDetail = async (token, disputeId) => {
  const res = await axios.get(`${WD_BASE}/${disputeId}`, { headers: authHeaders(token), timeout: 30000 });
  return camelizeDeep(res.data);
};

/** Streams the merchant CSV export and triggers a browser download. */
export const downloadDisputesCsv = async (token, filters = {}) => {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, val]) => {
    if (val !== undefined && val !== null && `${val}`.trim() !== "") params.set(key, `${val}`.trim());
  });
  const query = params.toString();
  const res = await axios.get(`${WD_BASE}/export${query ? `?${query}` : ""}`, {
    headers: authHeaders(token),
    responseType: "blob",
    timeout: 120000,
  });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `weight-disputes-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// ---------- consent actions (revision-scoped) ----------

/**
 * Accept the charge for the revision the merchant SAW. Pass expectedRevisionNumber from the
 * rendered detail — a 409 `weight_dispute_revised_since_viewed` means the amount changed
 * underneath them; refetch and re-show.
 */
export const acceptDispute = async (token, disputeId, expectedRevisionNumber) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/accept`,
    expectedRevisionNumber != null ? { expected_revision_number: expectedRevisionNumber } : {},
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

/** Raise a dispute: description + at least one confirmed own evidence id. Same 409 semantics. */
export const raiseDispute = async (token, disputeId, { description, evidenceIds, expectedRevisionNumber }) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/raise`,
    {
      description,
      evidence_ids: evidenceIds,
      ...(expectedRevisionNumber != null ? { expected_revision_number: expectedRevisionNumber } : {}),
    },
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

// ---------- evidence lifecycle ----------

export const requestEvidenceUpload = async (token, disputeId, file) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/evidence/request-upload`,
    { file_name: file.name, content_type: file.type, size_bytes: file.size },
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

/** Bare PUT straight to S3 — NO auth header (it would break the presigned signature). */
export const uploadEvidenceToS3 = async (presignedUploadUrl, file, contentType) => {
  await axios.put(presignedUploadUrl, file, {
    headers: { "Content-Type": contentType },
    timeout: 120000,
  });
};

export const confirmEvidenceUpload = async (token, disputeId, { evidenceId, s3ObjectKey, description }) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/evidence/confirm`,
    { evidence_id: evidenceId, s3_object_key: s3ObjectKey, description },
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

/** Link evidence: attach an external http(s) URL in one step — same windows/cap as files. */
export const addUrlEvidence = async (token, disputeId, { url, description }) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/evidence/url`,
    { url, description },
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

/** Soft delete own evidence: it greys out on both panels but never disappears. No undo. */
export const softDeleteEvidence = async (token, disputeId, evidenceId) => {
  const res = await axios.delete(`${WD_BASE}/${disputeId}/evidence/${evidenceId}`, {
    headers: authHeaders(token),
    timeout: 30000,
  });
  return camelizeDeep(res.data);
};

/** Versioned description edit on own ACTIVE evidence (previous value lands in history). */
export const editEvidenceDescription = async (token, disputeId, evidenceId, description) => {
  const res = await axios.post(
    `${WD_BASE}/${disputeId}/evidence/${evidenceId}/description`,
    { description },
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

/** inline=true → opens in the browser tab (view); false → force-download. Fresh URL per call. */
export const fetchEvidenceDownloadUrl = async (token, disputeId, evidenceId, inline = false) => {
  const res = await axios.get(
    `${WD_BASE}/${disputeId}/evidence/${evidenceId}/download-url${inline ? "?inline=true" : ""}`,
    { headers: authHeaders(token), timeout: 30000 }
  );
  return camelizeDeep(res.data);
};

// ---------- client-side evidence limits (server re-validates) ----------

export const EVIDENCE_ACCEPT = "image/jpeg,image/png,video/mp4,video/quicktime";

export const validateEvidenceFile = (file) => {
  const isImage = ["image/jpeg", "image/png"].includes(file.type);
  const isVideo = ["video/mp4", "video/quicktime"].includes(file.type);
  if (!isImage && !isVideo) return "Only JPEG/PNG images or MP4/MOV videos are allowed";
  const maxBytes = (isImage ? 10 : 50) * 1024 * 1024;
  if (file.size > maxBytes) return `File exceeds the ${isImage ? "10MB image" : "50MB video"} limit`;
  return null;
};

/** The 409 code meaning "amount changed underneath you — refetch and re-consent". */
export const REVISED_SINCE_VIEWED = "weight_dispute_revised_since_viewed";

export const errorCodeOf = (err) => err?.response?.data?.code ?? null;
export const errorMessageOf = (err) =>
  err?.response?.data?.message ?? err?.message ?? "Something went wrong";
